import _asyncToGenerator from "/home/runner/work/mainrepo/mainrepo/node_modules/.pnpm/@babel+runtime@7.29.2/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { getDiff, objectCloneDeep } from '@core/shared/utils';
import { instanceToInstance, plainToInstance } from 'class-transformer';
import { validate } from 'class-validator';
import * as deepmerge from 'deepmerge';
import 'reflect-metadata';
import { BehaviorSubject, firstValueFrom, from, Subject } from 'rxjs';
import { flatMap, map, mapTo } from 'rxjs/operators';
import { FOREVER_INVALID_NAME, foreverInvalid } from '../models';
import { getMergedClassTransformOptions, ValidationUtils } from '../utils';
import { evaluateParentConditionalValidation, validatorsOfType } from './build-validators';
import { DynamicFormArray } from './dynamic-form-array';
import { CLASS_VALIDATOR_GLOBAL_CONFIG_FORMBUILDER } from '@core/shared/model';
;
export class DynamicFormGroup extends FormGroup {
  constructor(factoryModel, meta, dynamicFormBuilderOptions, definition, fields, updateOn) {
    super({}, validatorsOfType(definition.validationFunctions, 'sync'), validatorsOfType(definition.validationFunctions, 'async'));
    this.factoryModel = factoryModel;
    this.meta = meta;
    this.dynamicFormBuilderOptions = dynamicFormBuilderOptions;
    this.definition = definition;
    this.fields = fields;
    this.nativeValidateErrors = new BehaviorSubject({});
    this.customValidateErrors = new BehaviorSubject({});
    this.objectChange = new Subject();
    this._validatorOptions = CLASS_VALIDATOR_GLOBAL_CONFIG_FORMBUILDER;
    this._updateOn = updateOn;
    this.fields = fields;
    this.formFields = onlyFields(fields);
    /*
    const classValidators = DynamicFormGroup.getClassValidators<TModel>(
    this.factoryModel,
    this.fields,
    this.defaultValidatorOptions
    );
    const formGroup = this._fb.group(
    classValidators
    );
    Object.keys(formGroup.controls).forEach(key => {
    this.addControl(key, formGroup.controls[key]);
    });
    this.valueChanges.subscribe(data => {
    this.validate(
    undefined,
    this.defaultValidatorOptions
    );
    });*/
  }
  subscribeToValueChanges(externalErrors, validatorOptions) {
    if (!this.valueChangesSubscription) {
      this.valueChangesSubscription = this.valueChanges.pipe(flatMap(data => {
        return from(this.validateAsync(externalErrors, validatorOptions)).pipe(mapTo(data));
      })).subscribe({
        error: err => {
          throw err;
        }
      });
    }
  }
  // Getters & Setters
  set externalErrors(externalErrors) {
    this._externalErrors = externalErrors;
    this.validate();
  }
  get externalErrors() {
    return this._externalErrors;
  }
  set validatorOptions(validatorOptions) {
    this._validatorOptions = validatorOptions;
    this.validate();
  }
  get validatorOptions() {
    return this._validatorOptions;
  }
  setValidatorOptionsAsync(validatorOptions) {
    var _this = this;
    return _asyncToGenerator(function* () {
      _this._validatorOptions = validatorOptions;
      return yield _this.validateAsync();
    })();
  }
  // Public API
  validate(externalErrors, validatorOptions) {
    this.validateAsync(externalErrors, validatorOptions).then(() => {}, error => {
      throw error;
    });
  }
  /**
   * Part 1 of validation handles marking of single FormControls as valid/invalid.
   * This implements Part 2 of validation setup:
   * call class-validator validation on entire form object
   * and add the collected error messages as
   */
  validateAsync(externalErrors, validatorOptions) {
    if (externalErrors === undefined) {
      externalErrors = objectCloneDeep(this._externalErrors || {});
    }
    if (validatorOptions === undefined) {
      validatorOptions = objectCloneDeep(this._validatorOptions || {});
    }
    return firstValueFrom(from(validate(this.getObject(), validatorOptions)).pipe(map(errors => {
      if (this.dynamicFormBuilderOptions.parentConditionalValidators?.length) {
        const shouldValidate = evaluateParentConditionalValidation(this, this.dynamicFormBuilderOptions.parentConditionalValidators);
        if (!shouldValidate) {
          ValidationUtils.markAsInvalidForExternalErrors({}, this.controls);
          this.setCustomErrors({});
          this.updateForcedInvalidFormState({});
          return;
        }
      }
      if (!externalErrors) externalErrors = {};
      const validationErrors = ValidationUtils.transformValidationErrors(errors);
      const allErrors = deepmerge(externalErrors, validationErrors);
      const registeredControlNames = Object.keys(this.fields);
      // class-validator will report errors based on the Model, not based on registered controls.
      // as we want to ignore errors for non-existent controls, we need to filter now.
      const errorsForDefinedControls = Object.keys(allErrors).reduce((filtered, key) => {
        if (registeredControlNames.includes(key)) {
          filtered[key] = allErrors[key];
        }
        return filtered;
      }, {});
      ValidationUtils.markAsInvalidForExternalErrors(externalErrors, this.controls);
      this.setCustomErrors(errorsForDefinedControls);
      this.updateForcedInvalidFormState(errorsForDefinedControls);
      //WARNING:  we cant always run updateValueAndValidity here cause it will cause an endless loop
    })));
  }
  /**
   * in case of class-validator returning any errors, we add an always-invalid FormControl.
   * This is to ensure the form will always be invalid if there are any errors, even if all created FormControls are valid.
   * It's hard to tell why this helps.
   * When removing this, the form validity status won't always be up to date.
   * The logic "enforces" form invalidity if there are any errors.
   * The extra control allows to track critical form validity changes.
   * In case of a detected validity change, it will trigger an updateValueAndValidity event.
   */
  updateForcedInvalidFormState(errors) {
    let changedForeverInvalid = false;
    const hasAnyErrorExceptForeverInvalid = Object.keys(errors).filter(key => key !== FOREVER_INVALID_NAME).length === 0;
    if (hasAnyErrorExceptForeverInvalid && this.get(FOREVER_INVALID_NAME)) {
      this.removeControl(FOREVER_INVALID_NAME);
      changedForeverInvalid = true;
    }
    if (this.valid && Object.keys(errors).length > 0 && !this.get(FOREVER_INVALID_NAME)) {
      this.addControl(FOREVER_INVALID_NAME, new FormControl('', [foreverInvalid]));
      changedForeverInvalid = true;
    }
    if (changedForeverInvalid) {
      this.updateValueAndValidity({
        onlySelf: true,
        emitEvent: false
      });
    }
  }
  // this is only to allow external logic to see/subscribe to errors. Nothing of this has any effect internally.
  // I think this must not change any of the DFGs state, it is only allowed to add information.
  setCustomErrors(errorsObj) {
    this.formErrors = errorsObj;
    const collectedNativeErrors = this.collectErrors(this);
    this.customValidateErrors.next(this.formErrors);
    this.nativeValidateErrors.next(collectedNativeErrors);
  }
  collectErrors(control, isRoot = true) {
    if (control.disabled) return {};
    if (control instanceof FormGroup) {
      const childErrors = Object.entries(control.controls).reduce((collection, [key, childControl]) => {
        if (key === 'foreverInvalid') return collection;
        const nestedErrors = this.collectErrors(childControl, false);
        if (Object.keys(nestedErrors).length > 0) {
          // console.log('found nested errors for '+ getControlName(control) + '.' + key,nestedErrors)
          const mergedChildErrors = {
            ...(collection[key] ? collection[key] : {}),
            ...nestedErrors
          };
          collection[key] = mergedChildErrors;
        }
        return collection;
      }, {});
      const errors = {
        ...(isRoot ? this.errors : {}),
        ...childErrors
      };
      return errors;
    } else {
      return control.errors || {};
    }
  }
  validateAllFormFields() {
    Object.keys(this.controls).forEach(field => {
      const control = this.get(field);
      // Control
      if (control instanceof FormControl) {
        control.markAsTouched({
          onlySelf: true
        });
      }
      // Group: recursive
      else if (control instanceof DynamicFormGroup) {
        control.validateAllFormFields();
      }
      // Array
      else if (control instanceof FormArray) {
        for (let i = 0; i < control.controls.length; i++) {
          const subctrl = control.controls[i];
          // Control in Array
          if (subctrl instanceof FormControl) {
            subctrl.markAsTouched({
              onlySelf: true
            });
          }
          // Group in Array: recursive
          else if (subctrl instanceof DynamicFormGroup) {
            subctrl.validateAllFormFields();
          }
        }
      }
    });
  }
  resetValidateAllFormFields() {
    ValidationUtils.markAsInvalidForExternalErrors({}, this.controls);
    Object.keys(this.controls).forEach(field => {
      const control = this.get(field);
      // Control
      if (control instanceof FormControl) {
        control.setErrors(null, {
          emitEvent: false
        });
        control.markAsUntouched({
          onlySelf: true
        });
        control.markAsPristine({
          onlySelf: true
        });
      }
      // Group: recursive
      else if (control instanceof DynamicFormGroup) {
        control.resetValidateAllFormFields();
      }
      // Array
      else if (control instanceof FormArray) {
        for (let i = 0; i < control.controls.length; i++) {
          const ctrl = control.controls[i];
          // Control in Array
          if (ctrl instanceof FormControl) {
            ctrl.setErrors(null, {
              emitEvent: false
            });
            ctrl.markAsUntouched({
              onlySelf: true
            });
            ctrl.markAsPristine({
              onlySelf: true
            });
          }
          // Group in Array: recursive
          else if (ctrl instanceof DynamicFormGroup) {
            ctrl.resetValidateAllFormFields();
          }
        }
      }
    });
    this.setCustomErrors({});
  }
  setExternalErrorsAsync(externalErrors) {
    var _this2 = this;
    return _asyncToGenerator(function* () {
      _this2._externalErrors = externalErrors;
      return yield _this2.validateAsync();
    })();
  }
  setExternalErrors(externalErrors) {
    this.setExternalErrorsAsync(externalErrors).then(() => {}, error => {
      throw error;
    });
  }
  getExternalErrors() {
    return this._externalErrors;
  }
  clearExternalErrors() {
    this.setExternalErrors({});
  }
  clearExternalErrorsAsync() {
    return this.setExternalErrorsAsync({});
  }
  // Helpers
  /**
  * Recursively gets all values from the form controls and all sub form group and array controls and returns it as
  * an object
  */
  getObject() {
    const classTransformOptions = getMergedClassTransformOptions(this.dynamicFormBuilderOptions);
    // Initialize the shape of the response
    const object = this._object ? instanceToInstance(this._object, {
      ...classTransformOptions,
      ignoreDecorators: true
    }) : this.factoryModel ? new this.factoryModel() : undefined;
    if (object !== undefined) {
      // Recursively get the value of all fields
      Object.keys(this.controls).filter(name => name !== FOREVER_INVALID_NAME).forEach(key => {
        // Handle Group
        if (this.controls[key] instanceof DynamicFormGroup) {
          object[key] = this.controls[key].getObject();
        } else if (this.controls[key] instanceof DynamicFormArray) {
          object[key] = this.controls[key].getValue();
        } else {
          object[key] = this.controls[key].value;
        }
      });
    }
    return this.factoryModel ? plainToInstance(this.factoryModel, object, {
      ...classTransformOptions,
      ignoreDecorators: true
    }) : object;
  }
  // NOTE: to comply with ng forms API, used e.g. by ng FormArray to update child values
  patchValue(patch, options = {}) {
    this.patchObject(patch);
    this.updateValueAndValidity(options);
  }
  /**
   * NOTE: something is very f****d up in async form control validation.
   * For some reason, the formGroup status can be stuck in PENDING.
   * Debugging was only possible to some degree.
   * I was able to debug this so far to understand that somehow the async validation is being executed with emitEvent=false.
   * However it is *impossible* to track down where this value originates from.
   * It seems that updateValueAndValidity/_runAsyncValidator does not emit the status change correctly.
   * However no reason for this behavior could be found.
   * FIX: we're hacking internal _updateControlsErrors function, compare old and new status, and force emitEvent.
   */
  _updateControlsErrors(emitEvent, changedControl, shouldHaveEmitted) {
    const oldStatus = this.status;
    this.status = this._calculateStatus();
    const statusUpdatedToValid = oldStatus !== 'VALID' && this.status === 'VALID';
    if (statusUpdatedToValid) emitEvent = true;
    // @ts-ignore
    super._updateControlsErrors(emitEvent, changedControl, shouldHaveEmitted);
  }
  patchObject(patch, options = {}) {
    return this.setObject({
      ...this.getObject(),
      ...patch
    }, options);
  }
  /**
  * Sets the value of every control on the form and recursively sets the values of the controls
  * on all sub form groups
  *
  * @param object the data to assign to all controls of the form group and sub groups
  * @param force - if false, identical object won't be re-patched
  */
  setObject(object, options = {}, force = false) {
    if (!object) throw new Error('setObject called with falsy object arg!');
    const hasChanges = force || Object.keys(getDiff(object, this.getObject)).length > 0;
    if (!hasChanges) return;
    const classTransformOptions = getMergedClassTransformOptions(this.dynamicFormBuilderOptions);
    if (object instanceof this.factoryModel) {
      this._object = instanceToInstance(object, {
        ...classTransformOptions,
        ignoreDecorators: true
      });
    } else {
      this._object = plainToInstance(this.factoryModel, object, {
        ...classTransformOptions,
        ignoreDecorators: true
      });
    }
    // Recursively set the value of all fields
    Object.keys(this.controls).forEach(key => {
      if (this.controls[key] instanceof DynamicFormGroup) {
        this.controls[key].setObject(this._object?.[key] || {}, options);
      } else if (this.controls[key] instanceof DynamicFormArray) {
        const arrayData = this._object ? this._object[key] : [];
        this.controls[key].setValue(arrayData, {
          emitEvent: false,
          onlySelf: true
        });
        // const fieldMeta = ClassTransformerMetadataHelpers.findMetadataForProperty(this.meta, key);
        // this.setValueForFormArray(this.controls[key], fieldMeta, arrayData);
      } else {
        // const newObject = this._object ? this._object[key] : [];
        const value = this._object[key];
        this.controls[key].setValue(value, {
          emitEvent: false,
          onlySelf: true
        });
      }
    });
    this.objectChange.next(this._object);
  }
}
export function createGenericFormStructureFromModelMetadata(factoryModel, data = null, classTransformOptions = {}) {
  let modifed = false;
  // const object: any = factoryModel ? new factoryModel(data) : data;
  if (!data) data = {};
  let object = factoryModel ? plainToInstance(factoryModel, data, classTransformOptions) : data;
  const fields = Object.keys(object);
  // if(first) console.log('create empty object',object)
  // if(!first) console.log('object created from modified', object,data)
  fields.forEach(fieldName => {
    // find array fields
    if (object[fieldName] && object[fieldName].length !== undefined) {
      // check if field has an existing object entry with constructor
      // if so, createEmptyObject from entries constructor - why??
      // probably to find sub-arrays!
      if (object[fieldName].length === 1 && Object.keys(object[fieldName][0]).length > 0 && object[fieldName][0].constructor) {
        object[fieldName] = [createGenericFormStructureFromModelMetadata(object[fieldName][0].constructor, object[fieldName][0], classTransformOptions)];
      }
      // if length is 0, add an empty entry and run createEmptyObject with modified data again.
      if (object[fieldName].length === 0) {
        data[fieldName] = [{}];
        modifed = true;
      }
    } else {
      data[fieldName] = undefined;
    }
  });
  if (modifed) {
    // console.log('xxx',data)
    // object = createGenericFormStructureFromModelMetadata(factoryModel, data, false);
  }
  // console.log('========== Resulting object',object)
  return object;
}
function onlyFields(fields) {
  const newFields = {};
  if (!fields) return newFields;
  Object.keys(fields).forEach(key => {
    if (fields[key] instanceof DynamicFormGroup) {
      // Group: recursive
      newFields[key] = onlyFields(fields[key].formFields);
    } else {
      // Array
      if (fields[key] instanceof FormArray) {
        if (fields[key].controls[0] instanceof DynamicFormGroup) {
          // Group within Array: recursive
          newFields[key] = onlyFields(fields[key].controls[0].formFields);
        } else {
          // Control within Array
          newFields[key] = fields[key].controls[0].value;
        }
      } else {
        // Handle Control
        newFields[key] = fields[key];
      }
    }
  });
  return newFields;
}